import type { Appearance } from "@stripe/stripe-js";
import type { ColourScheme } from "./widgetTheme";

export const MULISH_FONT_CSS_SRC = "https://fonts.googleapis.com/css2?family=Mulish:wght@400;600;700&display=swap";

const RESTING_INPUT_BORDER_COLOUR = "#828D98"; // $color-border-medium in styles/_variables.scss
const MULISH_FONT_FALLBACK = "'Mulish', sans-serif";
const DEFAULT_FONT_FAMILY = "Mulish, sans-serif";

const readCustomProperty = (styles: CSSStyleDeclaration | null, property: string, fallback: string): string => {
  const value = styles?.getPropertyValue(property).trim();
  if (!value) return fallback;
  return value;
};

const hasMulishFallback = (fontFamily: string): boolean =>
  fontFamily.replace(/['"]/g, "").replace(/\s/g, "").toLowerCase().endsWith("mulish,sans-serif");

const appendMulishFallback = (fontFamily: string): string => {
  const value = fontFamily.trim();
  if (!value) return DEFAULT_FONT_FAMILY;
  return hasMulishFallback(value) ? value : `${value}, ${MULISH_FONT_FALLBACK}`;
};

// Dark-only Appearance variables resolved from the resolved --gc-widget-*
// tokens. Only called for the dark scheme, so light does no extra work.
const darkVariables = (styles: CSSStyleDeclaration | null): Appearance["variables"] => {
  const secondary = readCustomProperty(styles, "--gc-widget-text-secondary", "#9A8A7A");
  const success = readCustomProperty(styles, "--gc-widget-success", "#4CAF6E");
  const warning = readCustomProperty(styles, "--gc-widget-warning", "#F0B84A");
  const onAccent = readCustomProperty(styles, "--gc-widget-accent-fg", "#F2EBE4");
  const primary = readCustomProperty(styles, "--gc-color-primary", "#007A7A");
  const text = readCustomProperty(styles, "--gc-color-text-primary", "#1B232A");
  const danger = readCustomProperty(styles, "--gc-color-error", "#dc3545");

  return {
    colorTextSecondary: secondary,
    colorSuccess: success,
    colorWarning: warning,
    accessibleColorOnColorPrimary: onAccent,
    accessibleColorOnColorBackground: text,
    iconColor: secondary,
    iconHoverColor: text,
    iconCardErrorColor: danger,
    iconCardCvcColor: secondary,
    iconCardCvcErrorColor: danger,
    iconCheckmarkColor: onAccent,
    iconChevronDownColor: secondary,
    iconChevronDownHoverColor: text,
    iconCloseColor: secondary,
    iconCloseHoverColor: text,
    iconLoadingIndicatorColor: secondary,
    iconMenuColor: secondary,
    iconMenuHoverColor: text,
    iconRedirectColor: secondary,
    tabIconColor: secondary,
    tabIconHoverColor: text,
    tabIconSelectedColor: primary,
    tabIconMoreColor: secondary,
    tabIconMoreHoverColor: text,
  };
};

// Light keeps Stripe's `stripe` base and exactly the variable set below, so
// the widget's light fields are unchanged. Dark starts from the `night` base
// and merges in the widget's tokens for secondary text, validation, on-accent
// text and the icon/tab glyphs Stripe's hosted fields paint.
export function buildWidgetStripeAppearance(element: HTMLElement | null, scheme: ColourScheme = "light"): Appearance {
  const styles = element ? getComputedStyle(element) : null;
  const fontFamily = appendMulishFallback(styles?.fontFamily ?? "");
  const primary = readCustomProperty(styles, "--gc-color-primary", "#007A7A");
  const focus = readCustomProperty(styles, "--gc-color-focus", primary);
  const text = readCustomProperty(styles, "--gc-color-text-primary", "#1B232A");
  const placeholder = readCustomProperty(styles, "--gc-color-text-light", "#A7AFB8");
  const background = readCustomProperty(styles, "--gc-color-bg-white", "#fff");
  const danger = readCustomProperty(styles, "--gc-color-error", "#dc3545");
  const restingBorder = readCustomProperty(styles, "--gc-widget-payment-field-border", RESTING_INPUT_BORDER_COLOUR);

  return {
    theme: scheme === "dark" ? "night" : "stripe",
    labels: "above",
    variables: {
      fontFamily,
      fontSizeBase: "14px",
      fontWeightNormal: "400",
      borderRadius: "8px",
      colorPrimary: primary,
      colorText: text,
      colorTextPlaceholder: placeholder,
      colorBackground: background,
      colorDanger: danger,
      ...(scheme === "dark" ? darkVariables(styles) : {}),
    },
    rules: {
      ".Input, .Tab, .Block, .CheckboxInput, .CodeInput": {
        border: `1px solid ${restingBorder}`,
        borderRadius: "8px",
        boxShadow: "none",
      },
      ".Input": {
        padding: "8px 10px",
        lineHeight: "22px",
        letterSpacing: "0.5px",
      },
      ".Input:focus": {
        border: `2px solid ${focus}`,
        boxShadow: "none",
        outline: "none",
        padding: "7px 9px",
      },
      ".Label, .Tab, .TabLabel, .Text, .TermsText, .Error": {
        fontSize: "14px",
        letterSpacing: "0.5px",
        color: text,
      },
      ".Tab:hover": {
        boxShadow: "none",
      },
      ".Tab--selected": {
        border: `2px solid ${primary}`,
        boxShadow: "none",
        color: primary,
      },
      ".Tab:focus, .Tab--selected:focus": {
        border: `2px solid ${focus}`,
        boxShadow: "none",
        color: primary,
      },
      ".Label": {
        marginBottom: "10px",
      },
      ".Error": {
        color: danger,
      },
    },
  };
}
